
import { useEffect, useState } from "react";
import { EmailInput } from "./EmailInput";
import { LoadingSpinner } from "./LoadingSpinner";
import { useEmailHandler } from "@/hooks/useEmailHandler";
import { VerificationInput } from "./VerificationInput";
import type { GameAttempt } from "@/services/emailService";
import { validateEmail } from "@/lib/validation/email-validation";
import { Button } from "@/components/ui/button";

interface EmailCaptureProps {
  onComplete: (email: string) => void;
  onExistingResult?: (attemptData: GameAttempt) => void;
}

export function EmailCapture({ onComplete, onExistingResult }: EmailCaptureProps) {
  const {
    email,
    setEmail,
    isLoading,
    handleStoredEmail,
    handleSubmit,
    needsVerification,
    setNeedsVerification,
    isVerifying,
    verificationError,
    handleResendLink
  } = useEmailHandler(onComplete, onExistingResult);
  
  const [validationMessage, setValidationMessage] = useState("");

  useEffect(() => {
    const storedEmail = localStorage.getItem("user_email");
    if (storedEmail) {
      handleStoredEmail(storedEmail);
    }
  }, [handleStoredEmail]);

  const handleEmailChange = (value: string) => {
    setEmail(value);
    
    if (value) {
      const validation = validateEmail(value);
      if (!validation.valid) {
        setValidationMessage(validation.reason || "Please enter a valid email");
      } else {
        setValidationMessage("");
      }
    } else {
      setValidationMessage("");
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="flex items-center justify-center">
      <div className="w-full space-y-4 bg-white p-6 rounded-lg shadow-lg">
        {!needsVerification ? (
          <>
            <div className="text-center space-y-2">
              <h2 className="text-2xl font-semibold">Sign up to play</h2>
              <p className="text-gray-600">Enter your email to access the daily puzzle</p>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <EmailInput
                  email={email}
                  isLoading={isLoading}
                  onChange={handleEmailChange}
                />
                {validationMessage && (
                  <p className="text-sm text-red-500 mt-1">{validationMessage}</p>
                )}
              </div>
              <button
                type="submit"
                className="w-full px-5 py-3 bg-black text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 text-lg"
                disabled={isLoading || !!validationMessage}
              >
                {isLoading ? "Sending..." : "Continue to Game"}
              </button>
            </form>
          </>
        ) : (
          <div className="space-y-6">
            <VerificationInput 
              onVerify={() => {}} 
              isVerifying={isVerifying}
              error={verificationError}
              email={email}
              onResend={handleResendLink}
            />
            
            <Button 
              variant="ghost"
              onClick={() => setNeedsVerification(false)}
              disabled={isVerifying}
            >
              Use a different email
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
